import React, { useState } from 'react';
import {
  MaintenanceTypesResponse,
  OrganizationUser,
  MaintenanceRecordCreate,
  MaintenanceType,
  MaintenanceStatus,
} from '../../../../../types/api';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  Autocomplete,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AddEntityDialog } from '../../../../common/AddEntityDialog';
import { useErrorHandler } from '../../../../../hooks/useErrorHandler';

interface MaintenanceFormFieldsProps {
  record: MaintenanceRecordCreate;
  maintenanceTypes: MaintenanceTypesResponse | null;
  loadingTypes: boolean;
  organizationUsers: OrganizationUser[];
  loadingUsers: boolean;
  onFieldChange: (
    field: keyof MaintenanceRecordCreate,
    value: string | MaintenanceType[] | undefined
  ) => void;
  onAddMaintenanceType?: (label: string) => Promise<{ value: string; label: string }>;
}

export const MaintenanceFormFields: React.FC<MaintenanceFormFieldsProps> = ({
  record,
  maintenanceTypes,
  loadingTypes,
  organizationUsers,
  loadingUsers,
  onFieldChange,
  onAddMaintenanceType,
}) => {
  const [showAddTypeDialog, setShowAddTypeDialog] = useState(false);
  const { showError } = useErrorHandler();

  if (loadingTypes || loadingUsers) {
    return (
      <Box display="flex" alignItems="center" gap={2} py={2}>
        <CircularProgress size={20} />
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <TextField
        label="Drone Serial"
        value={record.drone_serial}
        disabled
        fullWidth
        variant="filled"
      />

      <Autocomplete
        options={organizationUsers}
        getOptionLabel={(option) => option.full_name || option.email}
        value={organizationUsers.find((u) => u.id === record.performed_by) || null}
        onChange={(_, newValue) => {
          onFieldChange('performed_by', newValue?.id || '');
        }}
        data-testid="maintenance-performed-by"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Performed By"
            placeholder="Select who performed the maintenance"
            required
            helperText="Select the person who performed this maintenance"
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body2">{option.full_name || 'Unknown'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.email}
              </Typography>
            </Box>
          </li>
        )}
      />

      <Box display="flex" gap={1} alignItems="flex-start">
        <Autocomplete
          sx={{ flex: 1 }}
          multiple
          disableCloseOnSelect
          data-testid="maintenance-types-select"
          options={maintenanceTypes?.maintenance_types || []}
          getOptionLabel={(option) => option.label}
          groupBy={(option) => {
            if (option.value === 'other') return 'Other';
            if (option.value.startsWith('vi_') || option.value === 'all_visual_inspections')
              return 'Visual Inspections';
            return 'Component Maintenance';
          }}
          // Wrap in parentheses to ensure fallback array is used before calling .filter()
          value={(maintenanceTypes?.maintenance_types || []).filter((type) =>
            record.maintenance_types.includes(type.value)
          )}
          onChange={(_, newValue) => {
            onFieldChange(
              'maintenance_types',
              newValue.map((v) => v.value)
            );
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.label}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Maintenance Types"
              placeholder="Select one or more types"
              required
              helperText="Select at least one maintenance type"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.label} {...getTagProps({ index })} key={option.value} />
            ))
          }
        />

        {/* Add New Type Button */}
        {onAddMaintenanceType && (
          <Tooltip title="Add new maintenance type">
            <IconButton onClick={() => setShowAddTypeDialog(true)} sx={{ mt: 1 }} color="primary">
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Add New Type Dialog */}
      {onAddMaintenanceType && (
        <AddEntityDialog
          open={showAddTypeDialog}
          onClose={() => setShowAddTypeDialog(false)}
          onAdd={async (name) => {
            try {
              const newType = await onAddMaintenanceType(name);
              // Automatically select the newly created type
              onFieldChange('maintenance_types', [...record.maintenance_types, newType.value]);
            } catch (err) {
              showError(err, 'Failed to create maintenance type');
              throw err; // Re-throw to keep dialog open
            }
          }}
          title="Add New Maintenance Type"
          label="Type Name"
          addingLabel="Adding..."
        />
      )}

      <TextField
        label="Work Description"
        multiline
        rows={3}
        value={record.work_description}
        onChange={(e) => onFieldChange('work_description', e.target.value)}
        fullWidth
        required
        placeholder="Describe the maintenance work performed, parts replaced, observations, etc..."
        helperText="Required: Detailed description of maintenance activities"
        inputProps={{ 'data-testid': 'maintenance-work-description' }}
      />

      <Box display="flex" gap={2}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={record.status}
            label="Status"
            onChange={(e) => onFieldChange('status', e.target.value as MaintenanceStatus)}
          >
            <MenuItem value="planned">Planned</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Note: Multi-day functionality is now handled automatically via start/end date fields */}
    </Box>
  );
};
